import { useMemo, useRef, useState } from "react";
import SectionCard from "@/components/base/SectionCard";
import { useToast } from "@/components/base/Toast";
import Stars from "@/components/base/Stars";
import { currentStore } from "@/mocks/dashboard";
import {
  paymentMethods as initialMethods,
  storeDocuments,
  storeProfile,
} from "@/mocks/store";
import type { StoreDocument } from "@/types";
import CashPolicyModal from "./components/CashPolicyModal";
import StoreDocumentsCard from "./components/StoreDocumentsCard";

interface FormState {
  brandName: string;
  description: string;
  phone: string;
  whatsapp: string;
  email: string;
  commercialRegister: string;
  vatNumber: string;
  city: string;
  address: string;
}

const INITIAL_FORM: FormState = {
  brandName: storeProfile.brandName,
  description: storeProfile.description,
  phone: storeProfile.phone,
  whatsapp: storeProfile.whatsapp,
  email: storeProfile.email,
  commercialRegister: storeProfile.commercialRegister,
  vatNumber: storeProfile.vatNumber,
  city: storeProfile.city,
  address: storeProfile.address,
};

// Fields that require admin approval before going live.
const APPROVAL_FIELDS: (keyof FormState)[] = [
  "brandName",
  "description",
  "phone",
  "email",
  "commercialRegister",
  "vatNumber",
  "address",
];

const FIELDS: {
  key: keyof FormState;
  label: string;
  dir?: "ltr";
  type?: string;
  area?: boolean;
}[] = [
  { key: "brandName", label: "اسم العلامة التجارية" },
  { key: "description", label: "نبذة عن المحل", area: true },
  { key: "phone", label: "هاتف المحل", dir: "ltr", type: "tel" },
  { key: "whatsapp", label: "رقم الواتساب", dir: "ltr", type: "tel" },
  { key: "email", label: "البريد الإلكتروني", dir: "ltr", type: "email" },
  { key: "commercialRegister", label: "رقم السجل التجاري", dir: "ltr" },
  { key: "vatNumber", label: "الرقم الضريبي", dir: "ltr" },
  { key: "city", label: "المدينة" },
  { key: "address", label: "العنوان بالتفصيل" },
];

export default function StorePage() {
  const { show } = useToast();
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [baseline, setBaseline] = useState<FormState>(INITIAL_FORM);
  const [dataStatus, setDataStatus] = useState<"published" | "pending">(
    "published",
  );
  const [methods, setMethods] = useState(initialMethods);
  const [documents, setDocuments] = useState<StoreDocument[]>(storeDocuments);
  const [error, setError] = useState("");
  const [cover, setCover] = useState(storeProfile.cover);
  const [logo, setLogo] = useState(storeProfile.logo);
  const [cashAccepted, setCashAccepted] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [policyMode, setPolicyMode] = useState<"enable" | "view">("enable");
  const coverInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const needsReview = useMemo(
    () => APPROVAL_FIELDS.some((key) => form[key] !== baseline[key]),
    [form, baseline],
  );

  const handleCoverChange = (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      show("الرجاء اختيار صورة صحيحة (JPG أو PNG)", "error");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      show("حجم صورة الغلاف أكبر من 5 ميجابايت", "error");
      return;
    }
    setCover(URL.createObjectURL(file));
    show("تم تحديث صورة الغلاف مباشرة");
  };

  const handleLogoChange = (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      show("الرجاء اختيار صورة صحيحة (JPG أو PNG)", "error");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      show("حجم شعار المحل أكبر من 5 ميجابايت", "error");
      return;
    }
    setLogo(URL.createObjectURL(file));
    show("تم تحديث شعار المحل مباشرة");
  };

  const update = (key: keyof FormState, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const openPolicy = (mode: "enable" | "view") => {
    setPolicyMode(mode);
    setPolicyOpen(true);
  };

  const toggleMethod = (key: string) => {
    if (key === "cash") {
      const isEnabled = methods.find((item) => item.key === "cash")?.enabled;
      if (!isEnabled && !cashAccepted) {
        openPolicy("enable");
        return;
      }
    }
    setMethods((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, enabled: !item.enabled } : item,
      ),
    );
  };

  const acceptCashPolicy = () => {
    setCashAccepted(true);
    setPolicyOpen(false);
    setMethods((prev) =>
      prev.map((item) => (item.key === "cash" ? { ...item, enabled: true } : item)),
    );
    show("تم تفعيل الدفع النقدي بعد الموافقة على السياسة");
  };

  const reuploadDocument = (key: string) => {
    setDocuments((prev) =>
      prev.map((doc) =>
        doc.key === key
          ? { ...doc, status: "review" as const, rejectReason: undefined }
          : doc,
      ),
    );
    show("تم رفع المستند وإرساله للمراجعة", "warning");
  };

  const handleReset = () => {
    setForm(INITIAL_FORM);
    setBaseline(INITIAL_FORM);
    setDataStatus("published");
    setError("");
    setCover(storeProfile.cover);
    setLogo(storeProfile.logo);
    show("تمت استعادة البيانات الأصلية", "warning");
  };

  const handleSave = () => {
    if (!form.brandName.trim()) {
      setError("اسم العلامة التجارية مطلوب");
      show("الرجاء إدخال اسم العلامة التجارية", "error");
      return;
    }
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) {
      setError("صيغة البريد الإلكتروني غير صحيحة");
      show("صيغة البريد الإلكتروني غير صحيحة", "error");
      return;
    }
    setError("");

    if (needsReview) {
      setBaseline(form);
      setDataStatus("pending");
      show("تم إرسال التعديلات للمراجعة");
      return;
    }

    setBaseline(form);
    show("تم حفظ التغييرات");
  };

  return (
    <div className="space-y-4">
      <section className="yc-fade-up bg-white rounded-card border border-background-200 shadow-card overflow-hidden">
        <div className="group relative w-full h-40 sm:h-52 md:h-60 xl:h-72 bg-background-100 overflow-hidden">
          <img
            src={cover}
            alt="صورة غلاف المحل"
            title="غلاف المحل"
            className="w-full h-full object-cover object-top transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/15 to-foreground-950/25"></div>

          <div className="absolute top-3 end-4 flex items-center gap-2 opacity-0 translate-y-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0">
            <button
              type="button"
              onClick={() => coverInputRef.current?.click()}
              className="inline-flex items-center gap-2 h-9 rounded-full bg-white/90 backdrop-blur px-4 text-xs font-semibold text-foreground-900 hover:bg-white transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-image-edit-line"></i>
              تغيير الغلاف
            </button>
          </div>

          <div className="absolute bottom-3 end-4 hidden md:flex items-center gap-1.5 text-[11px] font-medium text-white/80">
            <i className="ri-aspect-ratio-line"></i>
            المقاس المثالي للغلاف 1600 × 500 بكسل
          </div>

          <input
            ref={coverInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(event) => handleCoverChange(event.target.files?.[0])}
          />
        </div>
        <div className="px-5 pt-3 pb-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex items-end gap-4">
              <div className="group -mt-12 relative w-20 h-20 shrink-0 rounded-box border-4 border-white bg-white overflow-hidden shadow-card">
                <img
                  src={logo}
                  alt={form.brandName}
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  title="تغيير الشعار (يُحدَّث مباشرة)"
                  aria-label="تغيير شعار المحل"
                  className="absolute inset-0 flex items-center justify-center bg-foreground-950/50 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer"
                >
                  <i className="ri-camera-line text-xl"></i>
                </button>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) => handleLogoChange(event.target.files?.[0])}
                />
              </div>
              <div className="pb-1">
                <h1 className="text-xl font-bold text-foreground-950">
                  {form.brandName || "اسم المحل"}
                </h1>
                <div className="mt-1 flex items-center gap-2">
                  <Stars value={currentStore.rating} size="text-sm" />
                  <span className="text-xs text-foreground-500">
                    {currentStore.rating} ({currentStore.ratingCount} تقييم)
                  </span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-background-100 px-3 py-1.5 text-xs font-medium text-foreground-600">
                <i className="ri-camera-lens-line"></i>
                الشعار يُحدَّث مباشرة
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1.5 text-xs font-semibold text-primary-800">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>
                {currentStore.statusLabel}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1.5 text-xs font-semibold text-accent-900">
                <i className="ri-restaurant-2-line"></i>
                مطعم
              </span>
            </div>
          </div>
        </div>
      </section>

      {dataStatus === "pending" && (
        <section className="yc-fade-up rounded-card border border-accent-300 bg-accent-50 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-800 text-lg">
              <i className="ri-time-line"></i>
            </span>
            <div>
              <h2 className="text-sm font-bold text-accent-900">
                بيانات المحل قيد المراجعة
              </h2>
              <p className="mt-1 text-xs leading-6 text-accent-900/80">
                التعديلات التي تحتاج اعتمادًا (اسم العلامة، الوصف، الهاتف، البريد،
                السجل التجاري، الرقم الضريبي، العنوان) أُرسلت للإدارة للمراجعة.
                بياناتك الحالية تبقى منشورة للعملاء حتى يتم اعتماد التعديلات.
              </p>
            </div>
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 space-y-4">
          <SectionCard
            title="بيانات المحل"
            subtitle="تظهر هذه البيانات للعملاء في صفحة الطلب"
            icon="ri-store-2-line"
          >
            <div className="space-y-4">
              {FIELDS.map((field) => (
                <div key={field.key}>
                  <label className="flex items-center gap-2 text-sm font-medium text-foreground-800 mb-2">
                    {field.label}
                    {APPROVAL_FIELDS.includes(field.key) && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-background-100 px-2 py-0.5 text-[10px] font-medium text-foreground-500">
                        <i className="ri-shield-check-line"></i>
                        يحتاج اعتمادًا
                      </span>
                    )}
                  </label>
                  {field.area ? (
                    <textarea
                      value={form[field.key]}
                      maxLength={300}
                      onChange={(event) =>
                        update(field.key, event.target.value)
                      }
                      rows={3}
                      className="w-full rounded-btn border border-background-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary-300 resize-none"
                    ></textarea>
                  ) : (
                    <input
                      type={field.type ?? "text"}
                      dir={field.dir}
                      value={form[field.key]}
                      onChange={(event) =>
                        update(field.key, event.target.value)
                      }
                      className={`w-full h-11 rounded-btn border border-background-200 px-3 text-sm outline-none focus:ring-2 focus:ring-primary-300 ${
                        field.dir === "ltr" ? "text-left" : ""
                      }`}
                    />
                  )}
                </div>
              ))}

              {error && <p className="text-xs text-red-500">{error}</p>}

              <div className="rounded-btn border border-background-200 bg-background-50 px-4 py-3 text-xs leading-6 text-foreground-600">
                <i className="ri-information-line me-1"></i>
                يتم تحديث <strong>شعار المحل</strong> مباشرة، أما تعديل اسم العلامة
                أو الوصف أو الهاتف أو البريد أو السجل التجاري أو الرقم الضريبي أو
                العنوان فيُرسل إلى الإدارة للمراجعة، وتبقى البيانات الحالية منشورة
                حتى الاعتماد.
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-1">
                <button
                  type="button"
                  onClick={handleSave}
                  className="inline-flex items-center justify-center gap-2 h-11 rounded-btn bg-primary-500 px-6 text-sm font-semibold text-white hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  <i
                    className={
                      needsReview ? "ri-send-plane-line" : "ri-save-line"
                    }
                  ></i>
                  {needsReview
                    ? "إرسال التعديلات للمراجعة"
                    : "حفظ التغييرات"}
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="inline-flex items-center justify-center h-11 rounded-btn bg-background-100 px-6 text-sm font-semibold text-foreground-700 hover:bg-background-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  استعادة
                </button>
              </div>
            </div>
          </SectionCard>
        </div>

        <div className="space-y-4">
          <SectionCard
            title="وسائل الدفع المقبولة"
            subtitle="فعّل الوسائل المتاحة لعملائك"
            icon="ri-bank-card-line"
            iconClass="bg-accent-100 text-accent-800"
          >
            <ul className="space-y-2.5">
              {methods.map((method) => (
                <li
                  key={method.key}
                  className="flex items-center justify-between rounded-btn border border-background-200 px-4 py-3"
                >
                  <span className="inline-flex items-center gap-3 text-sm font-medium text-foreground-800">
                    <span className="w-6 h-6 flex items-center justify-center text-foreground-500">
                      <i className={method.icon}></i>
                    </span>
                    {method.label}
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={method.enabled}
                    onClick={() => toggleMethod(method.key)}
                    className={`relative w-11 h-6 rounded-full transition-colors duration-200 cursor-pointer ${
                      method.enabled ? "bg-primary-500" : "bg-background-300"
                    }`}
                    aria-label={`تفعيل ${method.label}`}
                  >
                    <span
                      className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all duration-200 ${
                        method.enabled ? "start-5" : "start-0.5"
                      }`}
                    ></span>
                  </button>
                </li>
              ))}
            </ul>

            <button
              type="button"
              onClick={() => openPolicy("view")}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-primary-700 hover:underline cursor-pointer"
            >
              <i className="ri-file-text-line"></i>
              عرض سياسة الدفع النقدي
            </button>
          </SectionCard>

          <StoreDocumentsCard
            documents={documents}
            onReupload={reuploadDocument}
          />
        </div>
      </div>

      <CashPolicyModal
        open={policyOpen}
        mode={policyMode}
        onAccept={acceptCashPolicy}
        onClose={() => setPolicyOpen(false)}
      />
    </div>
  );
}